import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { UserRowView } from "../hooks/useUserRowViews";
import { OrganizationCell } from "./OrganizationCell";
import { SignInHealthCell } from "./SignInHealthCell";
import { UserAccessCell } from "./UserAccessCell";
import { UserIdentityCell } from "./UserIdentityCell";
import { UserRowActions } from "./UserRowActions";

export interface UsersCardsProps {
  views: readonly UserRowView[];
  loading: boolean;
  showOrganization: boolean;
  isDirectoryPending: boolean;
  pingedId: string | null;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      {/* Label above value, never beside it: at 320 px a `label: value` row wraps mid-value and the grid
          loses its alignment. */}
      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** The same rows below the `lg` breakpoint. A card is a reflowed row — same data, same order, same words. */
export function UsersCards({
  views,
  loading,
  showOrganization,
  isDirectoryPending,
  pingedId,
}: UsersCardsProps) {
  const { t } = useTranslations("users");

  if (loading) {
    return (
      <div className="grid gap-3 p-4 sm:grid-cols-2">
        {Array.from({ length: 4 }, (_, i) => (
          <ColocaSkeleton key={i} className="h-40 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-3 p-4 sm:grid-cols-2">
      {views.map((view) => (
        <ColocaSurface
          key={view.user.id}
          padding="md"
          className={cn(
            "flex flex-col gap-4",
            view.user.status === "suspended" && "opacity-55",
            view.isCurrentUser && "bg-primary/5",
            view.user.id === pingedId && "bg-primary/10 transition-colors duration-1000",
          )}
        >
          <div className="flex items-start justify-between gap-2">
            <button
              type="button"
              onClick={view.onOpenDetail}
              className="min-w-0 flex-1 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <UserIdentityCell user={view.user} isCurrentUser={view.isCurrentUser} />
            </button>
            <UserRowActions {...view.actions} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            {showOrganization && (
              <Field label={t("col_organization")}>
                <OrganizationCell
                  view={view.organization}
                  isDirectoryPending={isDirectoryPending}
                  isIdPending={view.isOrganizationPending}
                />
              </Field>
            )}
            <Field label={t("col_access")}>
              <UserAccessCell
                roles={view.roles}
                summary={view.summary}
                unavailable={view.rolesUnavailable}
                onOpen={view.onManageAccess}
              />
            </Field>
            <Field label={t("col_status")}>
              <ColocaStatusPill variant={view.statusVariant} withDot>
                {view.statusLabel}
              </ColocaStatusPill>
            </Field>
            <Field label={t("col_account_access")}>
              <SignInHealthCell health={view.health} />
            </Field>
          </div>
        </ColocaSurface>
      ))}
    </div>
  );
}
